import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTenant } from "@/lib/tenant";
import { useAuth } from "@/lib/auth";
import { PageHeader, Panel, SevBadge, Empty } from "@/components/Bits";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, ScanSearch, Ban, PowerOff } from "lucide-react";

const Dot = ({ ok }) => <span className={`inline-block w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-red-500"}`} />;

export default function Endpoints() {
  const { tenantId } = useTenant();
  const isCustomer = useAuth().user?.role === "customer";
  const [openId, setOpenId] = useState(null);
  const qc = useQueryClient();

  const { data = [] } = useQuery({
    queryKey: ["endpoints", tenantId],
    queryFn: () => api.get(`/endpoints/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
  });

  const detail = useQuery({
    queryKey: ["endpoint", openId],
    queryFn: () => api.get(`/endpoint/${openId}`).then((r) => r.data),
    enabled: !!openId,
  });

  const act = useMutation({
    mutationFn: ({ id, action }) => api.post(`/endpoint/${id}/action`, { action }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(d.message);
      qc.invalidateQueries({ queryKey: ["endpoints", tenantId] });
      qc.invalidateQueries({ queryKey: ["endpoint", openId] });
    },
  });

  if (!tenantId) return <Empty msg="Select an MSP tenant" />;

  const ep = detail.data?.endpoint;

  return (
    <>
      <PageHeader title="Endpoints" kicker={`${data.length} devices under management`} />
      <div className="p-6">
        <Panel>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b hairline">
                  <th className="px-4 py-3">Host</th>
                  <th className="px-3 py-3">User</th>
                  <th className="px-3 py-3">OS</th>
                  <th className="px-3 py-3">IP</th>
                  <th className="px-3 py-3">Defender</th>
                  <th className="px-3 py-3">Firewall</th>
                  <th className="px-3 py-3">BitLocker</th>
                  <th className="px-3 py-3">Updates</th>
                  <th className="px-3 py-3">Risk</th>
                </tr>
              </thead>
              <tbody>
                {data.map((e) => (
                  <tr key={e.id} data-testid={`endpoint-row-${e.id}`}
                    onClick={() => setOpenId(e.id)}
                    className="border-b hairline hover:bg-white/[0.04] cursor-pointer">
                    <td className="px-4 py-2.5 font-mono text-xs">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${e.online ? "bg-emerald-400" : "bg-zinc-600"}`} />
                      {e.hostname}{e.isolated && <span className="ml-2 text-[9px] text-red-400">[ISOLATED]</span>}
                      {e.source === "agent" && <span data-testid={`agent-badge-${e.id}`} className="ml-2 text-[9px] px-1 border border-emerald-400/50 text-emerald-300">LIVE AGENT</span>}
                    </td>
                    <td className="px-3 py-2.5 text-zinc-300">{e.user}</td>
                    <td className="px-3 py-2.5 text-zinc-400 text-xs">{e.os}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-zinc-400">{e.ip}</td>
                    <td className="px-3 py-2.5"><Dot ok={e.defender} /></td>
                    <td className="px-3 py-2.5"><Dot ok={e.firewall} /></td>
                    <td className="px-3 py-2.5"><Dot ok={e.bitlocker} /></td>
                    <td className="px-3 py-2.5 font-mono text-xs">{e.updates_pending}</td>
                    <td className="px-3 py-2.5"><SevBadge sev={e.risk} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <Sheet open={!!openId} onOpenChange={(v) => !v && setOpenId(null)}>
        <SheetContent side="right" className="bg-[#1B1B28] border-l border-white/10 w-[560px] sm:max-w-[560px] p-0">
          {ep && (
            <>
              <SheetHeader className="px-6 py-4 border-b hairline">
                <SheetTitle className="font-display font-extrabold text-lg tracking-tight flex items-center gap-2">
                  {ep.hostname} <SevBadge sev={ep.risk} />
                </SheetTitle>
                <div className="text-xs text-zinc-500 font-mono">{ep.user} · {ep.ip} · {ep.mac}</div>
              </SheetHeader>
              <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(100vh-90px)]">
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <Cell k="OS" v={ep.os} />
                  <Cell k="Build" v={ep.build} mono />
                  <Cell k="Last seen" v={new Date(ep.last_seen).toLocaleString()} />
                  <Cell k="Defender" v={ep.defender ? "Active" : "OFF"} tone={ep.defender ? "ok" : "critical"} />
                  <Cell k="Firewall" v={ep.firewall ? "Active" : "OFF"} tone={ep.firewall ? "ok" : "critical"} />
                  <Cell k="BitLocker" v={ep.bitlocker ? "Enabled" : "OFF"} tone={ep.bitlocker ? "ok" : "critical"} />
                  <Cell k="Updates pending" v={ep.updates_pending} tone={ep.updates_pending > 0 ? "high" : "ok"} />
                  <Cell k="Local admins" v={ep.local_admins} tone={ep.local_admins > 2 ? "high" : "ok"} />
                  <Cell k="Startup items" v={ep.startup_items} tone={ep.suspicious_startup ? "critical" : "ok"} />
                </div>

                {isCustomer ? (
                  <div data-testid="readonly-notice" className="border hairline p-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Read-only portal · response actions are performed by your MSP</div>
                ) : (
                <div className="grid grid-cols-2 gap-2">
                  <ActionBtn testid="secure-btn" onClick={() => act.mutate({ id: ep.id, action: "secure" })}
                    icon={ShieldCheck} label="SECURE ENDPOINT" tone="ai" />
                  <ActionBtn testid="scan-btn" onClick={() => act.mutate({ id: ep.id, action: "scan" })}
                    icon={ScanSearch} label="FULL SCAN" tone="ai" />
                  <ActionBtn testid="kill-btn" onClick={() => act.mutate({ id: ep.id, action: "kill_process" })}
                    icon={Ban} label="KILL SUSPICIOUS PROCESS" tone="warn" />
                  <ConfirmDanger
                    testid="isolate-btn"
                    triggerLabel={ep.isolated ? "RELEASE FROM ISOLATION" : "ISOLATE ENDPOINT"}
                    icon={PowerOff}
                    title={ep.isolated ? "Release from isolation?" : "Isolate this endpoint?"}
                    desc="This action will be logged in the audit trail. During isolation the endpoint can only reach the CyboScan management plane."
                    onConfirm={() => act.mutate({ id: ep.id, action: ep.isolated ? "release" : "isolate" })}
                  />
                </div>
                )}

                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Recent events</div>
                  <div className="border hairline divide-y divide-white/5 max-h-72 overflow-y-auto">
                    {(detail.data?.events || []).slice(0, 20).map((ev) => (
                      <div key={ev.id} className="px-3 py-2 text-xs flex items-start gap-2">
                        <SevBadge sev={ev.severity} />
                        <div className="flex-1 min-w-0">
                          <div className="text-zinc-200 truncate">{ev.detail}</div>
                          <div className="font-mono text-[10px] text-zinc-500">{ev.kind} · {ev.process}{ev.mitre ? ` · ${ev.mitre}` : ""} · {new Date(ev.ts).toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                    {(detail.data?.events || []).length === 0 && <Empty msg="No events" />}
                  </div>
                </div>

                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Vulnerabilities</div>
                  <div className="border hairline divide-y divide-white/5">
                    {(detail.data?.vulnerabilities || []).map((v) => (
                      <div key={v.id} className="px-3 py-2 text-xs flex items-center gap-3">
                        <SevBadge sev={v.severity} />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{v.software} <span className="text-zinc-500">{v.version}</span></div>
                          <div className="font-mono text-[10px] text-zinc-500">{v.cve} · CVSS {v.cvss}{v.patch_available ? " · patch available" : ""}</div>
                        </div>
                      </div>
                    ))}
                    {(detail.data?.vulnerabilities || []).length === 0 && <Empty msg="No known vulnerabilities" />}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

const Cell = ({ k, v, tone, mono }) => {
  const toneCls = { critical: "text-red-400", high: "text-orange-400", ok: "text-emerald-400" }[tone] || "text-white";
  return (
    <div className="border hairline p-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
      <div className={`text-xs font-semibold ${toneCls} ${mono ? "font-mono" : ""}`}>{v}</div>
    </div>
  );
};

const ActionBtn = ({ icon: Icon, label, onClick, tone, testid }) => {
  const cls = tone === "ai"
    ? "border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10"
    : tone === "warn"
    ? "border-orange-400/50 text-orange-400 hover:bg-orange-400/10"
    : "border-white/10 hover:bg-white/5";
  return (
    <button data-testid={testid} onClick={onClick} className={`border ${cls} px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center gap-2 transition-colors duration-150`}>
      <Icon size={13} /> {label}
    </button>
  );
};

const ConfirmDanger = ({ triggerLabel, title, desc, onConfirm, icon: Icon, testid }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button data-testid={testid} className="border border-red-900 bg-red-950/40 text-red-400 hover:bg-red-950/70 px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center gap-2 transition-colors duration-150">
        <Icon size={13} /> {triggerLabel}
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="bg-[#1B1B28] border-white/10 rounded-none">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display">{title}</AlertDialogTitle>
        <AlertDialogDescription className="text-zinc-400">{desc}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-none bg-transparent border-white/10">Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="confirm-isolate" onClick={onConfirm} className="rounded-none bg-red-600 hover:bg-red-500 text-white">Confirm</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
